import { useCallback, useEffect, useState } from 'react';

import { ChevronDownButton, ChevronUpButton } from 'components/Button';
import { CmsApiTableOfContentsParent } from 'modules/cms/api/types/CmsApiPage';
import { scrollIntoView } from 'utils/scrollIntoView';

import {
  MobileTableOfContentsChildRow,
  MobileTableOfContentsChildren,
  MobileTableOfContentsContainer,
  MobileTableOfContentsContent,
  MobileTableOfContentsHandle,
  MobileTableOfContentsParentRow,
  MobileTableOfContentsParentRowIcon,
} from './MobileTableOfContents.styles';

type Props = {
  parents: CmsApiTableOfContentsParent[];
  hasButtonBar: boolean;
};

export function CmsMobileTableOfContents({ parents, hasButtonBar }: Props) {
  const [open, setOpen] = useState(false);
  const [bottom, setBottom] = useState(-300);
  const [selectedParent, setSelectedParent] = useState<string | null>(null);

  let bottomOffset = 0;
  if (hasButtonBar) {
    const el = document.getElementById('button-bar-container');
    if (el) {
      bottomOffset = el.offsetHeight;
    }
  }

  useEffect(() => {
    if (open) {
      setBottom(bottomOffset);
    } else {
      const el = document.getElementById('MobileTableOfContentsContent');
      if (!el) {
        return;
      }
      setBottom(bottomOffset - el.offsetHeight);
    }
  }, [bottomOffset, open]);

  const navigateToChapter = useCallback((slug: string) => {
    setOpen(false);
    const el = document.getElementById(`${slug}`);
    if (el) {
      scrollIntoView(el);
    }
  }, []);

  const clickParent = (parent: CmsApiTableOfContentsParent) => {
    if (parent.url !== selectedParent && parent.children.length > 0) {
      setSelectedParent(parent.url);
    } else {
      navigateToChapter(parent.url);
    }
  };

  const clickChild = (childSlug: string) => {
    navigateToChapter(childSlug);
  };

  return (
    <MobileTableOfContentsContainer $bottom={bottom}>
      <MobileTableOfContentsHandle onClick={() => setOpen(!open)}>
        Table of Contents
      </MobileTableOfContentsHandle>
      <MobileTableOfContentsContent id="MobileTableOfContentsContent">
        {parents.map((parent) => (
          <>
            <MobileTableOfContentsParentRow onClick={() => clickParent(parent)}>
              <MobileTableOfContentsParentRowIcon>
                {parent.children.length > 0 &&
                  parent.url !== selectedParent && <ChevronDownButton />}
                {parent.url === selectedParent && (
                  <ChevronUpButton
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      setSelectedParent(null);
                    }}
                  />
                )}
              </MobileTableOfContentsParentRowIcon>
              <div>{parent.heading}</div>
            </MobileTableOfContentsParentRow>
            <MobileTableOfContentsChildren
              $open={parent.url === selectedParent}
            >
              {parent.children.map((child) => (
                <MobileTableOfContentsChildRow
                  onClick={() => {
                    clickChild(child.url);
                  }}
                >
                  {child.heading}
                </MobileTableOfContentsChildRow>
              ))}
            </MobileTableOfContentsChildren>
          </>
        ))}
      </MobileTableOfContentsContent>
    </MobileTableOfContentsContainer>
  );
}
